import styled, { css } from "styled-components";
import { hexToRGB } from "../../../utils/hex-to-rgb";
import { device } from "../../../../../style/theme";

export const Container = styled.div<{
  hasError?: boolean;
  isWrapper?: boolean;
}>`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  position: relative;
  ${(props) =>
    props.hasError &&
    `

  `}
  ${({ isWrapper, theme }) =>
    isWrapper &&
    `
    border: 1px solid ${theme.borderColor};
    padding: 12px;
  `}
`;

export const InfoWrapper = styled.div<{ isTooltipKeyLeftSide?: boolean }>`
  display: flex;
  flex-grow: 1;
  justify-content: ${({ isTooltipKeyLeftSide }) =>
    isTooltipKeyLeftSide ? "flex-start" : "space-between"};
  align-items: center;

  @media ${device.tablet} {
    width: 100%;
  }
`;

export const LabelContainer = styled.div<{ isTooltipKeyLeftSide?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ isTooltipKeyLeftSide }) =>
    !isTooltipKeyLeftSide &&
    css`
      flex: 1 1 0;
    `}
  padding-right: ${({ isTooltipKeyLeftSide }) =>
    isTooltipKeyLeftSide ? 8 : 16}px;

  @media ${device.tablet} {
    padding-right: ${({ isTooltipKeyLeftSide }) =>
      isTooltipKeyLeftSide ? 4 : 16}px;
  }
`;

export const Label = styled.label<{
  styleGuideErr?: boolean;
  isLabelSaturated?: boolean;
}>`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.5px;
  color: ${({ styleGuideErr, theme, isLabelSaturated }) =>
    styleGuideErr
      ? hexToRGB(theme.red, 0.5)
      : hexToRGB(theme.blue, isLabelSaturated ? 1 : 0.7)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SubLabel = styled(Label)`
  font-size: 10px;
`;

export const InputAdornment = styled.span`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.5px;
  text-align: right;
  position: absolute;
  right: 12px;
  top: 12px;
  opacity: 0.6;
  color: ${({ theme }) => theme.blue};
`;

export const Wrapper = styled.div<{ isColumn?: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: ${({ isColumn }) => (isColumn ? "flex-start" : "center")};
  order: 2;
  flex: 2.125;
  flex-direction: ${({ isColumn }) => (isColumn ? "column" : "row")};

  &:hover {
    & + label {
      color: ${(props) => hexToRGB(props.theme.blue, 0.8)};
    }
  }

  @media ${device.tablet} {
    width: 100%;
    flex-direction: column;
  }
`;

export const InputWrapper = styled.div<{ isColumn?: boolean }>`
  position: relative;
  ${({ isColumn }) =>
    isColumn &&
    css`
      margin-top: 8px;
    `}
  @media ${device.tablet} {
    width: 100%;
  }
`;

export const InputField = styled.input<{
  hasError?: boolean;
  isDisabled?: boolean;
  isDisabledWhite?: boolean;
  readOnly?: boolean;
  backgroundColor?: string;
  isValue?: boolean;
  isNoBorder?: boolean;
  isSaturatedBorder?: boolean;
  isPlaceholderCenter?: boolean;
  styleGuideErr?: boolean;
  isInputAdornment?: boolean;
}>`
  cursor: pointer;
  max-width: 280px;
  min-width: 280px;
  height: 40px;
  padding: 12px ${({ isInputAdornment }) => (isInputAdornment ? 40 : 12)}px 12px
    12px;
  border-radius: 3px;
  //touch-action: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  text-align: ${({ isPlaceholderCenter }) =>
    isPlaceholderCenter ? "center" : "left"};

  ${(props) =>
    props.styleGuideErr
      ? `background: ${props.theme.redBackground} !important;`
      : ""}

  background-color: ${(props) =>
    props.hasError
      ? props.theme.redBackground
      : props.backgroundColor ?? props.theme.white};
  outline: none;
  border: ${({ isNoBorder }) => (isNoBorder ? 0 : 1)}px solid
    ${({ theme, isValue, isSaturatedBorder }) =>
      isValue || isSaturatedBorder ? theme.grey : theme.borderColor};
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px
      ${(props) =>
        props.hasError
          ? props.theme.redBackground
          : props.backgroundColor ?? props.theme.white}
      inset !important;
  }

  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.blue};
  ${(props) =>
    props.hasError &&
    `
      & + label{
        color: ${hexToRGB(props.theme.red, 0.5)};
      }
  `}
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:not([type="radio"]):not([type="checkbox"]) {
    -webkit-appearance: none;
    border-radius: 3px;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield !important;
  }

  /* clears the ‘X’ from Chrome */
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }

  &::placeholder {
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: 0.5px;
    color: ${(props) => hexToRGB(props.theme.blue, 0.6)};
  }
  &:hover {
    cursor: pointer;
    border-color: ${(props) => props.theme.borderFocus};
    & + label {
      color: ${(props) => hexToRGB(props.theme.blue, 0.8)};
    }
    &::placeholder {
      color: ${(props) => hexToRGB(props.theme.blue, 0.8)};
    }
  }
  &:active,
  &:focus {
    cursor: text;
    background-color: ${({ backgroundColor }) => backgroundColor || "#fff"};
    border-color: ${(props) => props.theme.borderFocus};
    color: ${(props) => props.theme.blue};
    & + label {
      color: ${(props) => props.theme.blue};
    }
    &::placeholder {
      color: ${(props) => hexToRGB(props.theme.blue, 0.5)};
    }
  }
  ${({ isDisabled, theme }) =>
    isDisabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  @media ${device.tablet} {
    width: 100%;
    max-width: 100%;
  }
`;

export const CalendarContainer = styled.div<{
  visitingDays?: string[];
  visitedDates?: string[];
  paidDates?: string[];
}>`
  display: flex;
  justify-content: center;
  max-width: 280px;
  .react-calendar {
    border: none;
  }
  .react-calendar__navigation {
    margin-bottom: 0;
  }

  .react-calendar__navigation__label {
    pointer-events: none;
  }

  ${({ visitingDays }) =>
    visitingDays &&
    visitingDays.map(
      (item) => `.${item} {
            background-color: ${hexToRGB("#ff0000", 0.4)};
          }`,
    )}
  ${({ visitedDates, visitingDays }) =>
    visitingDays &&
    visitingDays.map(
      (item) =>
        visitedDates &&
        visitedDates.map(
          (date) => css`
            .${item}.date_${date} {
              background-color: ${hexToRGB("#ece000", 1)};
            }
          `,
        ),
    )}
    ${({ paidDates, visitingDays }) =>
    visitingDays &&
    visitingDays.map(
      (item) =>
        paidDates &&
        paidDates.map(
          (date) => css`
            .${item}.date_${date} {
              background-color: ${hexToRGB("#2abd00", 0.7)};
            }
          `,
        ),
    )}
    
    @media ${device.tablet} {
    max-width: 100%;
  }
`;
